import { useCallback, useRef, useState } from "react";
import type { SpeakerNotesSlideContent } from "../../../types";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { getErrorMessage, logSpeakerNotesError, postSpeakerNotes } from "../services/speakerNotesApi";
import { extractSelectedSlideContent } from "../services/slideContentExtractor";
import { setSpeakerNotes } from "../services/speakerNotesOffice";

export type SpeakerNotesState =
  | { status: "idle" }
  | { status: "generating" }
  | { status: "done"; notes: string }
  | { status: "applied"; notes: string }
  | { status: "error"; message: string };

/** User-facing error copy, centralized so it isn't repeated at each call site. */
const NO_SLIDE_CONTENT_MESSAGE = "Select a slide with some text or a table first.";
const GENERATION_FAILED_MESSAGE = "Speaker notes generation failed";
const APPLY_FAILED_MESSAGE = "Couldn't write to the Notes pane on this host — copy the text instead.";

function hasContent(slideContent: SpeakerNotesSlideContent): boolean {
  return slideContent.textContent.trim().length > 0 || slideContent.shapes.length > 0;
}

export function useSpeakerNotes() {
  const http = useDeckorateHttp();
  const [state, setState] = useState<SpeakerNotesState>({ status: "idle" });
  // Guards against a second generate starting while one is already in
  // flight, matching useBrandApply's double-click guard.
  const isGeneratingRef = useRef(false);

  const generate = useCallback(async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    setState({ status: "generating" });

    try {
      const slideContent = await extractSelectedSlideContent();
      if (!hasContent(slideContent)) {
        throw new Error(NO_SLIDE_CONTENT_MESSAGE);
      }
      const notes = await postSpeakerNotes(http, slideContent);
      setState({ status: "done", notes });
    } catch (error) {
      const message = getErrorMessage(error, GENERATION_FAILED_MESSAGE);
      logSpeakerNotesError("speaker-notes-generate", message);
      setState({ status: "error", message });
    } finally {
      isGeneratingRef.current = false;
    }
  }, [http]);

  const apply = useCallback(
    async (mode: "append" | "replace") => {
      if (state.status !== "done" && state.status !== "applied") return;
      const notes = state.notes;
      try {
        const ok = await setSpeakerNotes(notes, mode);
        if (ok) {
          setState({ status: "applied", notes });
        } else {
          logSpeakerNotesError("speaker-notes-apply", APPLY_FAILED_MESSAGE);
          setState({ status: "error", message: APPLY_FAILED_MESSAGE });
        }
      } catch (error) {
        const message = getErrorMessage(error, APPLY_FAILED_MESSAGE);
        logSpeakerNotesError("speaker-notes-apply", message);
        setState({ status: "error", message });
      }
    },
    [state],
  );

  const reset = useCallback(() => setState({ status: "idle" }), []);

  return { state, generate, apply, reset };
}
